import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import { FadeUp } from "@/components/site/_shared";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { EVIDENCE_PAGES } from "@/data/evidencePages";
import {
  organizationSchema,
  medicalWebPageSchema,
  breadcrumbSchema,
  evidenceOgImage,
  BRAND, SITE_URL} from "@/lib/seo";

const LAST_REVIEWED = "June 2026";

export const GRADE_STYLES = {
  A: "bg-primary text-primary-foreground border-primary",
  B: "bg-accent/10 text-accent border-accent",
  C: "bg-muted text-foreground/70 border-border",
  Emerging: "bg-background text-primary border-primary/40 border-dashed",
};

export function GradeBadge({ grade, small = false }) {
  return (
    <span
      className={`inline-flex items-center border font-mono uppercase tracking-[0.14em] font-semibold ${
        small ? "text-[0.55rem] px-2 py-0.5" : "text-[0.62rem] px-3 py-1"
      } ${GRADE_STYLES[grade] || GRADE_STYLES.C}`}
    >
      Evidence {grade}
    </span>
  );
}

export default function EvidencePage({ slug }) {
  const page = EVIDENCE_PAGES[slug];
  const url = `${SITE_URL}/evidence/${page.slug}`;

  useDocumentMeta({
    title: page.metaTitle,
    description: page.metaDescription,
    image: evidenceOgImage(page.slug),
    url,
    schema: [
      organizationSchema(),
      medicalWebPageSchema({ name: page.metaTitle, about: page.h1 }),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Evidence Library", url: "/evidence" },
        { name: page.h1, url: `/evidence/${page.slug}` },
      ]),
    ],
  });

  return (
    <div data-testid={`evidence-page-${page.slug}`} className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />

      <main className="pt-28 md:pt-32 pb-20 md:pb-28">
        <article className="max-w-3xl mx-auto px-4 md:px-8">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" data-testid="evidence-breadcrumb" className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-muted-foreground">
            <a href="/" className="hover:text-accent transition-colors">Home</a>
            <span aria-hidden className="mx-2 text-accent">/</span>
            <Link to="/evidence" className="hover:text-accent transition-colors">Evidence Library</Link>
            <span aria-hidden className="mx-2 text-accent">/</span>
            <span className="text-primary">{page.eyebrow.replace("Evidence · ", "")}</span>
          </nav>

          {/* Hero */}
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <div className="iw-overline">{page.eyebrow}</div>
            <GradeBadge grade={page.grade} />
          </div>
          <h1 className="mt-4 font-display font-bold tracking-tight leading-[1.05] text-[clamp(1.9rem,4.5vw,3.25rem)] text-primary">
            {page.h1}
          </h1>
          <p
            data-testid="evidence-direct-answer"
            className="mt-6 text-lg md:text-xl text-foreground/85 leading-relaxed font-medium"
          >
            {page.directAnswer}
          </p>

          {/* Byline */}
          <div
            data-testid="evidence-byline"
            className="mt-8 border border-border bg-card p-5 font-mono text-[0.68rem] leading-relaxed tracking-wide text-foreground/70 space-y-1"
          >
            <p>
              <span className="text-accent font-semibold">Written/Reviewed by:</span>{" "}
              <strong className="text-primary">{BRAND.medicalDirector.name}</strong> — Medical Director
            </p>
            <p>
              <span className="text-accent font-semibold">Scientific/Technical contribution:</span>{" "}
              <strong className="text-primary">{BRAND.founder.name}</strong>
            </p>
            <p>
              <span className="text-accent font-semibold">Last medical review:</span> {LAST_REVIEWED}
            </p>
          </div>

          {/* Sections */}
          <div className="mt-14 space-y-12">
            {page.sections.map((s) => (
              <FadeUp key={s.h} as="section" className="border-l-2 border-accent/40 pl-6">
                <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-primary leading-snug">
                  {s.h}
                </h2>
                <p className="mt-3 text-base md:text-lg text-foreground/75 leading-relaxed">{s.body}</p>
              </FadeUp>
            ))}
          </div>

          {/* Evidence summary table */}
          <FadeUp className="mt-16">
            <div className="iw-overline mb-5">Evidence Summary</div>
            <div data-testid="evidence-summary" className="divide-y divide-border border border-border bg-card">
              {page.evidenceSummary.map((row) => (
                <div key={row.finding} className="flex items-center justify-between gap-4 px-5 py-4">
                  <span className="text-sm md:text-base text-foreground/80 leading-snug">{row.finding}</span>
                  <GradeBadge grade={row.grade} small />
                </div>
              ))}
            </div>
            <p className="mt-3 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-muted-foreground">
              A = consistent RCT/meta-analytic · B = strong observational/mixed · C = limited · Emerging = active research
            </p>
          </FadeUp>

          {/* References */}
          <FadeUp className="mt-16">
            <div className="iw-overline mb-5">References</div>
            <ol data-testid="evidence-references" className="space-y-4 list-decimal list-inside">
              {page.references.map((r) => (
                <li key={r.url} className="text-sm md:text-base text-foreground/75 leading-relaxed">
                  {r.authors} ({r.year}).{" "}
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-semibold hover:text-accent transition-colors underline decoration-accent/40 underline-offset-2"
                  >
                    {r.title}
                  </a>
                  . <em>{r.journal}</em>.
                </li>
              ))}
            </ol>
          </FadeUp>

          {/* Related + CTA */}
          <FadeUp className="mt-16">
            <div className="iw-overline">Related</div>
            <div data-testid="evidence-related" className="mt-5 flex flex-wrap gap-3">
              {page.related.map((r) => (
                <a key={r.href} href={r.href} className="iw-btn-ghost inline-flex items-center gap-2 px-5 py-2.5 text-[0.62rem]">
                  {r.label} <span aria-hidden>→</span>
                </a>
              ))}
            </div>
            <div className="mt-12 border-2 border-primary/25 bg-card p-8 text-center">
              <div className="font-display text-xl md:text-2xl font-bold text-primary tracking-tight">
                See what your own data says.
              </div>
              <a
                href="/get-started"
                data-testid="evidence-bottom-cta"
                className="mt-6 iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]"
              >
                Start My Health Assessment <span aria-hidden>→</span>
              </a>
            </div>
          </FadeUp>
        </article>
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
